import React, { useState, useEffect, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import Select from "react-select";
import { Toaster, toast } from "react-hot-toast";
import Pagination from "../../components/Pagination";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { parseApiPagination } from "../../utils/parseApiPagination";
import {
  listGirviBanks,
  postGirviBankLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../../erpClient";
import { generateGirviBankLedgerPdfBlob } from "../../utils/generateGirviBankLedgerPdf";

function processGirviBankLedgerEntries(
  entries,
  { entireReport, fromDate, toDate, responseMetalType },
) {
  const firstEntry = entries[0];
  const hasMetalType =
    responseMetalType === 1 ||
    (firstEntry &&
      ("gold_aavak" in firstEntry ||
        "gold_jaavak" in firstEntry ||
        "total_gold" in firstEntry ||
        "silver_aavak" in firstEntry ||
        "silver_jaavak" in firstEntry ||
        "total_silver" in firstEntry));

  const sortedEntries = [...entries].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    if (dateA.getTime() === dateB.getTime()) return 0;
    return dateA - dateB;
  });

  let openingAmountBalance = 0;
  let transactionsInPeriod = sortedEntries;

  if (!entireReport && fromDate) {
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);

    const openingEntries = [];
    const periodEntries = [];

    sortedEntries.forEach((entry) => {
      const entryDate = new Date(entry.date);
      entryDate.setHours(0, 0, 0, 0);

      if (entryDate < from) {
        openingEntries.push(entry);
      } else if (!toDate || entryDate <= new Date(toDate)) {
        periodEntries.push(entry);
      }
    });

    if (hasMetalType && openingEntries.length > 0) {
      const lastEntry = openingEntries[openingEntries.length - 1];
      const total_gold = parseFloat(lastEntry.total_gold) || 0;
      const total_silver = parseFloat(lastEntry.total_silver) || 0;
      openingAmountBalance = total_gold + total_silver;
    } else {
      openingEntries.forEach((entry) => {
        openingAmountBalance +=
          (parseFloat(entry.aavak_amt) || 0) -
          (parseFloat(entry.jaavak_amt) || 0);
      });
    }

    transactionsInPeriod = periodEntries;
  } else {
    openingAmountBalance = 0;
  }

  let amountBalance = openingAmountBalance;

  const ledgerData = transactionsInPeriod.map((entry) => {
    let amount_credit = 0;
    let amount_debit = 0;
    let total_balance = 0;

    if (hasMetalType) {
      const gold_credit = parseFloat(entry.gold_aavak) || 0;
      const gold_debit = parseFloat(entry.gold_jaavak) || 0;
      const silver_credit = parseFloat(entry.silver_aavak) || 0;
      const silver_debit = parseFloat(entry.silver_jaavak) || 0;
      amount_credit = gold_credit + silver_credit;
      amount_debit = gold_debit + silver_debit;
      amountBalance += amount_credit - amount_debit;
      total_balance = amountBalance;
    } else {
      amount_credit = parseFloat(entry.aavak_amt) || 0;
      amount_debit = parseFloat(entry.jaavak_amt) || 0;
      amountBalance += amount_credit - amount_debit;
      total_balance = amountBalance;
    }

    const decimalPlaces = hasMetalType ? 3 : 2;

    return {
      date: entry.date || "-",
      credit: amount_credit ? amount_credit.toFixed(decimalPlaces) : "",
      debit: amount_debit ? amount_debit.toFixed(decimalPlaces) : "",
      balance: total_balance
        ? total_balance.toFixed(decimalPlaces)
        : `0.${"0".repeat(decimalPlaces)}`,
      remarks: entry.remarks || "",
      type: entry.type || "",
      isOpening: false,
    };
  });

  return { ledgerData, hasMetalType };
}

function GirviBankLedger() {
  const { buttonClass, cardClass, tableClass, buttonTableStyle } =
    useButtonTableStyle();
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: 44,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
      borderColor: "#e0e6eb",
      boxShadow: "none",
      "&:hover": { borderColor: "#e0e6eb" },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
      zIndex: 9999,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0.25rem 0.65rem",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  const [selectedBank, setSelectedBank] = useState(null);
  const [bankOptions, setBankOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [isMetalType, setIsMetalType] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const response = await listGirviBanks({});
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.girvi_banks)
          ? response.data.girvi_banks
          : [];

      if (rows.length > 0) {
        const options = rows.map((bank) => ({
          value: bank.id,
          label: bank.bank_name || `Girvi Bank ${bank.id}`,
          bankName: bank.bank_name,
          accountHolder: bank.holder_name,
          accountNumber: bank.account_number,
        }));
        setBankOptions(options);
      } else {
        setBankOptions([]);
      }
    } catch (error) {
      setBankOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  // Clean up blob url on unmount
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = () => {
      fetchBanks();
      setLedgerRows([]);
      setPdfData(null);
      setShowPDFModal(false);
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
      setPdfBlobUrl(null);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);
    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const handleSubmit = async (
    e,
    { page = currentPage, limit = itemsPerPage, bypassCache = false } = {},
  ) => {
    if (e?.preventDefault) e.preventDefault();
    if (!selectedBank?.value) return;

    const refresh = Boolean(e) || bypassCache;
    const cacheKey = entireReport
      ? `${selectedBank?.value || ""}:${page}:${limit}`
      : "";
    if (refresh) pageCacheRef.current.clear();
    if (cacheKey && !refresh) {
      const cached = pageCacheRef.current.get(cacheKey);
      if (cached) {
        setLedgerRows(cached.ledgerRows);
        setPdfData(cached.pdfData);
        setIsMetalType(cached.isMetalType);
        setCurrentPage(cached.currentPage);
        setTotalCount(cached.totalCount);
        setHasMorePages(cached.hasMorePages);
        setLoading(false);
        return;
      }
    }
    setLoading(true);

    const apiPayload = {
      bid: selectedBank.value,
    };

    if (entireReport) {
      apiPayload.type = "entire";
      apiPayload.page = page;
      apiPayload.limit = limit;
    } else {
      apiPayload.filter_type = "range";
      if (fromDate) apiPayload.from_date = fromDate;
      if (toDate) apiPayload.to_date = toDate;
    }

    try {
      const payload = await postGirviBankLedger(apiPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message || "Failed to fetch ledger data. Please try again.",
        );
        setLedgerRows([]);
        setPdfData(null);
        return;
      }

      const entries = payload.data?.entries || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found for this Bank / date range.");
        setLedgerRows([]);
        setPdfData(null);
        setTotalCount(0);
        return;
      }

      const { ledgerData: finalLedgerData, hasMetalType } =
        processGirviBankLedgerEntries(entries, {
          entireReport,
          fromDate,
          toDate,
          responseMetalType: payload.data?.metal_type,
        });

      setIsMetalType(hasMetalType);
      setLedgerRows(finalLedgerData);

      const paginationMeta = entireReport
        ? parseApiPagination(payload, finalLedgerData.length)
        : null;

      if (entireReport && paginationMeta) {
        setCurrentPage(paginationMeta.page);
        setTotalCount(paginationMeta.total);
        setHasMorePages(paginationMeta.hasMore);
        if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
      } else {
        setCurrentPage(1);
        setTotalCount(finalLedgerData.length);
        setHasMorePages(false);
      }

      const nextPdf = {
        ledgerData: finalLedgerData,
        bankInfo: selectedBank,
        fromDate,
        toDate,
        entireReport,
      };
      setPdfData(nextPdf);
      if (cacheKey && paginationMeta) {
        pageCacheRef.current.set(cacheKey, {
          ledgerRows: finalLedgerData,
          pdfData: nextPdf,
          isMetalType: hasMetalType,
          currentPage: paginationMeta.page,
          totalCount: paginationMeta.total,
          hasMorePages: paginationMeta.hasMore,
        });
      }
      toast.success("Ledger generated successfully!");
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while fetching the ledger data.",
        ),
      );
      console.error("Error fetching girvi bank ledger:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedBank?.value) {
      toast.error("Please select a bank account first.");
      return;
    }
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      bid: selectedBank.value,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.page = 0;
      printPayload.limit = 0;
    } else {
      printPayload.filter_type = "range";
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postGirviBankLedger(printPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message ||
            "Failed to fetch ledger data for printing. Please try again.",
        );
        return;
      }

      const entries = payload.data?.entries || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found to print.");
        return;
      }

      const { ledgerData: processedLedgerData, hasMetalType } =
        processGirviBankLedgerEntries(entries, {
          entireReport,
          fromDate,
          toDate,
          responseMetalType: payload.data?.metal_type,
        });

      const bankInfo = {
        bankName: selectedBank?.bankName || selectedBank?.label || "",
        accountHolder: selectedBank?.accountHolder || "",
        accountNumber: selectedBank?.accountNumber || "",
        label: selectedBank?.label || "",
      };

      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      const blob = await generateGirviBankLedgerPdfBlob({
        ledgerData: processedLedgerData,
        bankInfo,
        fromDate,
        toDate,
        entireReport,
        isMetalType: hasMetalType,
      });

      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlobUrl(blobUrl);
      setShowPDFModal(true);
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data.",
        ),
      );
      console.error("Error generating girvi bank ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className={`card ${cardClass}`}>
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Girvi Bank Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                <div className="mb-3 col-md-3">
                  <label className="form-label">Select Bank Account</label>
                  <Select
                    options={bankOptions}
                    value={selectedBank}
                    onChange={setSelectedBank}
                    placeholder={
                      loading ? "Loading..." : "Search and select Bank..."
                    }
                    isClearable
                    isSearchable
                    isLoading={loading}
                    styles={selectStyles}
                  />
                </div>
                <div className="mb-3 col-md-2">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                <div className="mb-3 col-md-2">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="girviEntireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <div
                  className="mb-3 col-md-2 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={!selectedBank || loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>

            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Ledger - {selectedBank?.label ? selectedBank.label : ""}
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print"
                    )}
                  </button>
                </div>
                <div className={`table-responsive mb-4 border ${cardClass}`}>
                  <table
                    className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                  >
                    <thead className="text-dark fs-4">
                      <tr>
                        <th className="text-nowrap">Date</th>
                        <th className="text-nowrap">Type</th>
                        <th className="text-end text-nowrap text-success">
                          Credit
                        </th>
                        <th
                          className="text-end text-nowrap"
                          style={{ color: "#dc3545" }}
                        >
                          Debit
                        </th>
                        <th className="text-end text-nowrap">Balance</th>
                        <th className="text-nowrap">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => {
                        const cellStyle = row.isOpening
                          ? { backgroundColor: "#E3F2FD", fontWeight: 500 }
                          : undefined;
                        return (
                          <tr key={idx}>
                            <td style={cellStyle}>
                              {row.isOpening ? "-" : row.date}
                            </td>
                            <td style={cellStyle}>{row.type || "-"}</td>
                            <td
                              className={`text-end ${
                                row.credit ? "text-success" : ""
                              }`}
                              style={cellStyle}
                            >
                              {row.credit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.debit ? "#dc3545" : undefined,
                              }}
                            >
                              {row.debit || "-"}
                            </td>
                            <td
                              className="text-end"
                              style={{
                                ...cellStyle,
                                color: row.isOpening
                                  ? undefined
                                  : parseFloat(row.balance || 0) < 0
                                    ? "#dc3545"
                                    : parseFloat(row.balance || 0) > 0
                                      ? "#28a745"
                                      : undefined,
                              }}
                            >
                              {row.balance || "-"}
                            </td>
                            <td style={cellStyle}>{row.remarks || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {entireReport && totalCount > 0 && (
                  <Pagination
                    totalItems={totalCount}
                    hasMore={hasMorePages}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={(page) => {
                      if (ignoreNextPageFetchRef.current) {
                        ignoreNextPageFetchRef.current = false;
                        return;
                      }
                      handleSubmit(undefined, { page, limit: itemsPerPage });
                    }}
                    onItemsPerPageChange={(limit) => {
                      ignoreNextPageFetchRef.current = true;
                      setItemsPerPage(limit);
                      setCurrentPage(1);
                      handleSubmit(undefined, {
                        page: 1,
                        limit,
                        bypassCache: true,
                      });
                    }}
                    itemsPerPageOptions={[10, 20, 30, 50, 100]}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print Loading Modal */}
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing Girvi Bank Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title>Girvi Bank Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              title="Girvi Bank Ledger PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default GirviBankLedger;
